import { useEffect, useState, useCallback } from 'react';
import { getUser, generatePaymentLink, freeTrial, getPlan} from './apiService';

export type TUser = {
    userId: string;
    firstname: string;
    lastname: string;
    email: string;
    mobile: number | '';
    country: string;
    state: string;
};

export type plan = {
    planPrice: string;
    planName: number | string;
    planStartDate: string;
    planEndDate: string;
    paymentId: string;
    isPlanActive: boolean;
    cancelAtPeriodEnd: boolean;
    stripeSubscriptionId?: string;
    subscriptionPlanId?: string;
};

const useUserAPI = (token: string) => {
	const { response, error, loading, call } = getUser();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [userData, setUserData] = useState<TUser | undefined>();
	useEffect(() => {
		setUserData(response?.data as TUser);
		setIsLoading(false);
	}, [response, error]);

	useEffect(() => {
		if (!token) {
			setUserData(undefined);
			return;
		}
		call(token).subscribe({
			error: (e) => console.log(e),
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token]);

	return { userData, error, isLoading, setIsLoading };
};

const usePlanAPI = (token: string) => {
	const { response, error, loading, call } = getPlan();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [planData, setPlanData] = useState<plan | undefined>();
	useEffect(() => {
		setPlanData(response?.data as plan);
		setIsLoading(false);
	}, [response, error]);

	useEffect(() => {
		if (!token) {
			setPlanData(undefined);
			return;
		}
		call(token).subscribe({
			error: (e) => console.log(e),
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token]);

	return { planData, error, isLoading, setIsLoading };
};

export const useGeneratePaymentLink = () => {
    const [linkData, setLinkData] = useState<string | null>(null);
    const [responseData, setResponseData] = useState<any | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<any>(null);

    const { call } = generatePaymentLink();

    const trigger = useCallback((priceId: string): Promise<string | null> => {
        return new Promise((resolve, reject) => {
            if (!priceId) {
                const err = new Error('Price ID is required.');
                setError(err);
                reject(err);
                return;
            }

            setIsLoading(true);
            setError(null);

            call(priceId).subscribe({
                next: (res: any) => {
                    const paymentLink = res?.data?.url || null;
                    setLinkData(paymentLink);
                    setIsLoading(false);
                    resolve(paymentLink);
                },
                error: (err) => {
                    setError(err);
                    setIsLoading(false);
                    reject(err);
                },
            });
        });
    }, [call]);

    const { freeTrialCall } = freeTrial();

    const freeTrialTrigger = useCallback((priceId: string): Promise<string | null> => {
        return new Promise((resolve, reject) => {
            if (!priceId) {
                const err = new Error('Price ID is required.');
                setError(err);
                reject(err);
                return;
            }

            setIsLoading(true);
            setError(null);

            freeTrialCall(priceId).subscribe({
                next: (res: any) => {
                    const message = res?.msg;
                    setResponseData(message);
                    setIsLoading(false);
                    resolve(message);
                },
                error: (err) => {
                    setError(err);
                    setIsLoading(false);
                    reject(err);
                },
            });
        });
    }, [call]);

    return { linkData, isLoading, error, trigger, freeTrialTrigger, responseData};
};
export { useUserAPI, usePlanAPI } ;